import React from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Grid, 
  Paper,
  Button,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Card,
  CardContent,
  CardMedia
} from '@mui/material';
import { 
  CloudUpload as CloudUploadIcon,
  AutoAwesome as AutoAwesomeIcon,
  Edit as EditIcon,
  Description as DescriptionIcon,
  Psychology as PsychologyIcon
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import LandingHeader from '../components/LandingHeader';
import Footer from '../components/Footer';

const HowItWorksPage: React.FC = () => {
  const steps = [
    {
      label: 'Choose Your Method',
      description: 'Start by either uploading an existing document, selecting from our template library, or generating a new contract with AI by describing what you need.',
      icon: <CloudUploadIcon sx={{ color: '#2d8cff', fontSize: 30 }} />,
      image: '/images/step1.jpg'
    },
    {
      label: 'AI Analysis & Enhancement',
      description: "Our AI analyzes your document for legal risks and suggests improvements to strengthen your contract's clarity and enforceability.",
      icon: <PsychologyIcon sx={{ color: '#2d8cff', fontSize: 30 }} />,
      image: '/images/step2.jpg'
    },
    {
      label: 'Edit & Customize',
      description: 'Use our intuitive editor to make changes, accept AI suggestions, and customize the document to your specific requirements.',
      icon: <EditIcon sx={{ color: '#2d8cff', fontSize: 30 }} />,
      image: '/images/step3.jpg'
    },
    {
      label: 'Export & Share',
      description: 'Download your finalized document in multiple formats (PDF, DOCX, TXT) or share it securely with stakeholders for review and signature.',
      icon: <DescriptionIcon sx={{ color: '#2d8cff', fontSize: 30 }} />,
      image: '/images/step4.jpg'
    }
  ];

  return (
    <Box>
      {/* Hero Section */}
      <Box sx={{ 
        bgcolor: '#1a365d', 
        color: 'white',
        py: { xs: 8, md: 12 },
        position: 'relative',
        overflow: 'hidden',
        pt: 4
      }}>
        <LandingHeader />
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={7}>
              <Typography 
                variant="h2" 
                component="h1" 
                fontWeight="800" 
                gutterBottom
                sx={{ 
                  fontSize: { xs: '2rem', md: '3rem' },
                  lineHeight: 1.2
                }}
              >
                How Contract AI Works
              </Typography>
              <Typography 
                variant="subtitle1" 
                sx={{ 
                  mb: 4, 
                  opacity: 0.9,
                  fontSize: { xs: '1rem', md: '1.1rem' },
                  maxWidth: '600px'
                }}
              >
                Discover our simple four-step process that transforms complex legal document creation into a streamlined, efficient workflow.
              </Typography>
              <Button 
                variant="contained" 
                size="large"
                component={RouterLink}
                to="/documents/upload"
                startIcon={<CloudUploadIcon />}
                sx={{ 
                  bgcolor: '#2d8cff',
                  fontWeight: 600,
                  px: 4,
                  py: 1.5,
                  borderRadius: '8px',
                  '&:hover': {
                    bgcolor: '#1a7aee'
                  }
                }}
              >
                Try It Now
              </Button>
            </Grid>
          </Grid>
        </Container>
        
        {/* Background decoration */}
        <Box sx={{
          position: 'absolute',
          right: { xs: '-300px', md: '-150px' },
          top: '50%',
          transform: 'translateY(-50%)',
          width: '600px',
          height: '600px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(45,140,255,0.3) 0%, rgba(45,140,255,0) 70%)',
          zIndex: 0
        }} />
      </Box>
      
      {/* Process Steps - Desktop */}
      <Box sx={{ display: { xs: 'none', md: 'block' } }}>
        <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
          <Typography 
            variant="h3" 
            component="h2" 
            fontWeight="700" 
            textAlign="center"
            sx={{ 
              mb: 2,
              color: '#1a365d',
              fontSize: { xs: '1.75rem', md: '2.5rem' }
            }}
          >
            Our Simple Process
          </Typography>
          <Typography 
            variant="subtitle1" 
            textAlign="center" 
            color="text.secondary" 
            sx={{ 
              mb: 8, 
              maxWidth: '800px', 
              mx: 'auto',
              fontSize: { xs: '1rem', md: '1.1rem' }
            }}
          >
            Contract AI makes legal document creation and management effortless with just a few simple steps.
          </Typography>
          
          <Grid container spacing={6}>
            {steps.map((step, index) => (
              <Grid item xs={12} key={index}>
                <Paper 
                  elevation={0}
                  sx={{ 
                    p: 0, 
                    borderRadius: '16px',
                    overflow: 'hidden',
                    border: '1px solid rgba(0,0,0,0.05)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
                      transform: 'translateY(-5px)',
                    }
                  }}
                >
                  <Grid container>
                    <Grid item xs={12} md={5} sx={{ 
                      bgcolor: index % 2 === 0 ? '#f8faff' : 'white',
                      p: 4,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center'
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Box sx={{ 
                          width: 50, 
                          height: 50, 
                          borderRadius: '50%', 
                          bgcolor: 'rgba(45,140,255,0.1)', 
                          display: 'flex', 
                          justifyContent: 'center', 
                          alignItems: 'center',
                          mr: 2
                        }}>
                          <Typography variant="h5" fontWeight="bold" color="#2d8cff">{index + 1}</Typography>
                        </Box>
                        <Typography variant="h4" fontWeight="700" sx={{ color: '#1a365d' }}>
                          {step.label}
                        </Typography>
                      </Box>
                      <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.1rem', lineHeight: 1.6 }}>
                        {step.description}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={7}>
                      <Box 
                        sx={{ 
                          height: '100%', 
                          minHeight: '300px',
                          bgcolor: '#e0e0e0',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          p: 4
                        }}
                      >
                        <Box sx={{ textAlign: 'center' }}>
                          {step.icon}
                          <Typography variant="body1" sx={{ mt: 2, color: '#1a365d' }}>
                            Step {index + 1}: {step.label}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
      
      {/* Process Steps - Mobile */}
      <Box sx={{ display: { xs: 'block', md: 'none' } }}>
        <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
          <Typography 
            variant="h3" 
            component="h2" 
            fontWeight="700" 
            textAlign="center"
            sx={{ 
              mb: 2,
              color: '#1a365d',
              fontSize: { xs: '1.75rem', md: '2.5rem' }
            }}
          >
            Our Simple Process
          </Typography>
          <Typography 
            variant="subtitle1" 
            textAlign="center" 
            color="text.secondary" 
            sx={{ 
              mb: 4, 
              maxWidth: '800px', 
              mx: 'auto',
              fontSize: { xs: '1rem', md: '1.1rem' }
            }}
          >
            Contract AI makes legal document creation and management effortless with just a few simple steps.
          </Typography>
          
          <Stepper orientation="vertical" sx={{ mt: 4 }}>
            {steps.map((step, index) => (
              <Step active={true} key={index}>
                <StepLabel 
                  StepIconComponent={() => (
                    <Box sx={{ 
                      width: 40, 
                      height: 40, 
                      borderRadius: '50%', 
                      bgcolor: 'rgba(45,140,255,0.1)', 
                      display: 'flex', 
                      justifyContent: 'center', 
                      alignItems: 'center'
                    }}>
                      <Typography variant="body1" fontWeight="bold" color="#2d8cff">{index + 1}</Typography>
                    </Box>
                  )}
                >
                  <Typography variant="h6" fontWeight="700" sx={{ color: '#1a365d' }}>
                    {step.label}
                  </Typography>
                </StepLabel>
                <StepContent>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                    {step.description}
                  </Typography>
                  <Box sx={{ 
                    height: '150px', 
                    bgcolor: '#e0e0e0',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 3
                  }}>
                    <Box sx={{ textAlign: 'center' }}>
                      {step.icon}
                    </Box>
                  </Box>
                </StepContent>
              </Step>
            ))}
          </Stepper>
        </Container>
      </Box>
      
      {/* Benefits Section */}
      <Box sx={{ bgcolor: '#f8faff', py: { xs: 8, md: 10 } }}>
        <Container maxWidth="lg">
          <Typography 
            variant="h3" 
            component="h2" 
            fontWeight="700" 
            textAlign="center"
            sx={{ 
              mb: 2,
              color: '#1a365d',
              fontSize: { xs: '1.75rem', md: '2.5rem' }
            }}
          >
            Benefits of Using Contract AI
          </Typography>
          <Typography 
            variant="subtitle1" 
            textAlign="center" 
            color="text.secondary" 
            sx={{ 
              mb: 8, 
              maxWidth: '800px', 
              mx: 'auto',
              fontSize: { xs: '1rem', md: '1.1rem' }
            }}
          >
            Our platform delivers significant advantages for legal professionals and businesses of all sizes.
          </Typography>
          
          <Grid container spacing={4}>
            <Grid item xs={12} sm={6} md={3}>
              <Card 
                elevation={0}
                sx={{ 
                  height: '100%',
                  borderRadius: '16px',
                  border: '1px solid rgba(0,0,0,0.05)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
                    transform: 'translateY(-5px)',
                  }
                }}
              >
                <Box sx={{ p: 2, bgcolor: 'rgba(45,140,255,0.1)', display: 'flex', justifyContent: 'center' }}>
                  <Typography variant="h2" fontWeight="bold" color="#2d8cff">80%</Typography>
                </Box>
                <CardContent>
                  <Typography variant="h6" fontWeight="700" gutterBottom sx={{ color: '#1a365d' }}>
                    Time Saved
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Reduce document creation and review time by up to 80% with AI-powered automation.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Card 
                elevation={0}
                sx={{ 
                  height: '100%',
                  borderRadius: '16px',
                  border: '1px solid rgba(0,0,0,0.05)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
                    transform: 'translateY(-5px)',
                  }
                }}
              >
                <Box sx={{ p: 2, bgcolor: 'rgba(45,140,255,0.1)', display: 'flex', justifyContent: 'center' }}>
                  <Typography variant="h2" fontWeight="bold" color="#2d8cff">95%</Typography>
                </Box>
                <CardContent>
                  <Typography variant="h6" fontWeight="700" gutterBottom sx={{ color: '#1a365d' }}>
                    Error Reduction
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Minimize human errors with AI-powered contract review and analysis.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Card 
                elevation={0}
                sx={{ 
                  height: '100%',
                  borderRadius: '16px',
                  border: '1px solid rgba(0,0,0,0.05)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
                    transform: 'translateY(-5px)',
                  }
                }}
              >
                <Box sx={{ p: 2, bgcolor: 'rgba(45,140,255,0.1)', display: 'flex', justifyContent: 'center' }}>
                  <Typography variant="h2" fontWeight="bold" color="#2d8cff">60%</Typography>
                </Box>
                <CardContent>
                  <Typography variant="h6" fontWeight="700" gutterBottom sx={{ color: '#1a365d' }}>
                    Cost Reduction
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Lower legal costs by reducing the time spent on document creation and review.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Card 
                elevation={0}
                sx={{ 
                  height: '100%',
                  borderRadius: '16px',
                  border: '1px solid rgba(0,0,0,0.05)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
                    transform: 'translateY(-5px)',
                  }
                }}
              >
                <Box sx={{ p: 2, bgcolor: 'rgba(45,140,255,0.1)', display: 'flex', justifyContent: 'center' }}>
                  <Typography variant="h2" fontWeight="bold" color="#2d8cff">24/7</Typography>
                </Box>
                <CardContent>
                  <Typography variant="h6" fontWeight="700" gutterBottom sx={{ color: '#1a365d' }}>
                    Availability
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Access your documents and create new ones anytime, anywhere, on any device.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>
      
      {/* CTA Section */}
      <Container maxWidth="md" sx={{ py: { xs: 8, md: 10 } }}>
        <Box 
          sx={{ 
            textAlign: 'center',
            p: { xs: 4, md: 6 },
            borderRadius: '16px',
            border: '1px solid rgba(45,140,255,0.3)',
            bgcolor: 'white',
            boxShadow: '0 10px 40px rgba(0,0,0,0.06)'
          }}
        >
          <Typography 
            variant="h4" 
            fontWeight="700" 
            gutterBottom
            sx={{ 
              color: '#1a365d',
              fontSize: { xs: '1.5rem', md: '2rem' }
            }}
          >
            Ready to Get Started?
          </Typography>
          <Typography 
            variant="body1" 
            color="text.secondary" 
            sx={{ 
              mb: 4, 
              maxWidth: '600px', 
              mx: 'auto' 
            }}
          >
            Experience the power of AI-driven contract management. Sign up today and transform your legal workflow.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Button 
              variant="contained" 
              size="large"
              component={RouterLink}
              to="/register"
              startIcon={<AutoAwesomeIcon />}
              sx={{ 
                bgcolor: '#2d8cff',
                fontWeight: 600,
                px: 4,
                py: 1.5,
                borderRadius: '8px',
                '&:hover': {
                  bgcolor: '#1a7aee'
                }
              }}
            >
              Start Free Trial
            </Button>
            <Button 
              variant="outlined" 
              size="large"
              component={RouterLink}
              to="/documents/upload"
              sx={{ 
                borderColor: '#1a365d',
                color: '#1a365d',
                fontWeight: 600,
                px: 4,
                py: 1.5,
                borderRadius: '8px',
                '&:hover': {
                  borderColor: '#1a365d',
                  bgcolor: 'rgba(26,54,93,0.04)'
                }
              }}
            >
              See Demo
            </Button>
          </Box>
        </Box>
      </Container>
      <Footer />
    </Box>
  );
};

export default HowItWorksPage;
